<template>
  <div v-if="shouldShowSidebar()">
    <SideBar />
  </div>
  
  <div class="top-page">
    <router-view />
  </div>

  <!-- Interactive Demo Component -->
  <InteractiveDemo
    v-if="showDemo"
    ref="demo"
    @demo-start="onDemoStart"
    @demo-end="onDemoEnd"
    @demo-close="onDemoClose"
    @error="onDemoError"
  />
</template>

<script>
import { useRoute } from 'vue-router'
import SideBar from '@/components/sidebar/SideBar'
import { sidebarWidth } from '@/components/sidebar/state'
import { FAVICON_PATH, ROUTES_WITHOUT_SIDEBAR } from '@/assets/constants/constants'
import { InteractiveDemo, shouldShowDemo, markDemoAsCompleted } from '@/demo'
import eventEmitter from '@/utils/eventEmitter';


export default {
  components: {
    SideBar,
    InteractiveDemo
  },
  setup() {
    return {
      sidebarWidth,
      route: useRoute()
    }
  },
  data() {
    return {
      showDemo: false,
      demoStarted: false,
      userClosedDemo: false
    };
  },
  computed: {
    topPageMargin() {
      return this.shouldShowSidebar() ? sidebarWidth : '0px';
    }
  },
  mounted() {
    this.changeFavicon(FAVICON_PATH);
    
    // Restore userClosedDemo flag from localStorage
    this.userClosedDemo = localStorage.getItem('userClosedDemo') === 'true';
    
    // Initialize demo for first-time users (but NOT on user profile page)
    this.initializeDemoIfNeeded();
    
    // Watch for route changes to start demo when user navigates away from profile
    this.$watch(
      () => this.$route.path,
      () => {
        this.initializeDemoIfNeeded();
      }
    );

    // Listen for run-demo event from "Run Demo" button
    this.handleRunDemo = () => {
      this.userClosedDemo = false;
      localStorage.removeItem('userClosedDemo');
      this.showDemo = true;
      this.$nextTick(() => {
        if (this.$refs.demo && !this.demoStarted) {
          this.$refs.demo.start();
          this.demoStarted = true;
        }
      });
    };
    eventEmitter.on('run-demo', this.handleRunDemo);
  },
  beforeUnmount() {
    // Clean up the run-demo event listener
    eventEmitter.off('run-demo', this.handleRunDemo);
  },
  methods:{
    isAuthenticated(){
      return this.$store.state.auth.token !== null;
    },
    shouldShowSidebar() {
      return this.isAuthenticated() && !ROUTES_WITHOUT_SIDEBAR.includes(this.$route.path);
    },
    changeFavicon(iconPath) {
      var link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = iconPath;
      document.getElementsByTagName('head')[0].appendChild(link);
    },
    // Demo initialization
    initializeDemoIfNeeded() {
      // Only exclude non-authenticated and landing pages
      const excludedRoutes = ['/login', '/register', '/password', '/'];
      if (excludedRoutes.includes(this.$route.path)) {
        return;
      }

      // Don't auto-show if user manually closed the demo
      if (this.userClosedDemo) {
        return;
      }

      // Only show for authenticated first-time users
      if (this.isAuthenticated() && shouldShowDemo()) {
        this.showDemo = true;
        // Auto-start demo after component renders
        this.$nextTick(() => {
          if (this.$refs.demo && !this.demoStarted) {
            this.$refs.demo.start();
            this.demoStarted = true;
          }
        });
      }
    },
    // Demo event handlers
    onDemoStart() {
      console.log('[Demo] Demo started');
      // You can disable certain features during demo if needed
    },
    onDemoEnd() {
      console.log('[Demo] Demo completed');
      markDemoAsCompleted();
      this.showDemo = false;
      this.demoStarted = false;
      // Demo is complete, user can now freely use the app
    },
    onDemoClose() {
      console.log('[Demo] Demo closed by user');
      this.showDemo = false;
      this.demoStarted = false;
      this.userClosedDemo = true;
      localStorage.setItem('userClosedDemo', 'true');
    },
    onDemoError(error) {
      console.error('[Demo] Error occurred:', error);
      // Optionally handle error - you could show a toast notification
    }
  }
}
</script>


<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
.top-page {
  margin-top: v-bind(topPageMargin);
}

/* Unified Page Layout */
.page-container {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  padding: 0;
  margin: 0;
  width: 100%;
}

.app-content-container {
  position: absolute;
  left: v-bind(sidebarWidth);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin-top: 45px;
  padding: 1rem;
  width: calc(100% - v-bind(sidebarWidth));
  box-sizing: border-box;
  min-height: calc(100vh - 45px);
}

.app-content-container.page-layout > * {
  max-width: none;
}

</style>
